import { Toast } from "primereact/toast";
import { MasterTabs } from "../MasterTabs";
import { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { ErrorMessage, Form, Formik } from "formik";
import { InputText } from "primereact/inputtext";
import { DataTable } from "primereact/datatable";
import { Toolbar } from "primereact/toolbar";
import {
  defaultPageRowOptions,
  defaultRowOptions,
  paginatorLinks,
  showingEntries,
  SkeletonbodyTemplate,
  Skeletonitems,
  statusDropdownItems,
} from "../../../appconfig/Settings";
import { FilterMatchMode } from "primereact/api";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { SprayProblemColumns } from "../../../appconfig/DataTableColumns";
import { globalNameFilterTemplate } from "../../../appconfig/AppHelper";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { sprayProblemInterFace } from "../../../appconfig/interfaces";
import {
  getAllDetails,
  postAllData,
  updateData,
} from "../../../service/ProductService";
import { ProblemValidationSchema } from "../../../config/Validate";
import {
  ADD_PROBLEM,
  CHANGE_PROBLEM_STATUS,
  GET_PROBLEM_LIST,
  UPDATE_PROBLEM,
} from "../../../config/ApiConstant";

const SprayForProblemList = () => {
  document.title = "Spray For Problem | Agriown";

  //  ref hook
  const formikRef = useRef<any>(null);

  // Set Toast
  const toast = useRef<Toast>(null);

  // state for Filter
  const [globalFilter, setGlobalFilter] = useState<any>(null);

  // state for dialog
  const [addSprayProDialog, setAddSprayProDialog] = useState<boolean>(false);

  // state for loading
  const [pageLoad, setPageLoad] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  // state for store spray problem list
  const [sprayProblemList, setSprayProblemList] = useState<
    sprayProblemInterFace[]
  >([]);

  const [editMode, setEditMode] = useState<boolean>(false);

  const [selectedSprayProblem, setSelectedSprayProblem] = useState<any>([]);

  const [confirmStatusDialog, setConfirmStatusDialog] =
    useState<boolean>(false);
  const [statusChangeData, setStatusChangeData] = useState<{
    id: number;
    newStatus: boolean;
  } | null>(null);
  const [statusFilterValue, setStatusFilterValue] = useState<any>(null);

  // state for slot selection
  const emptyFilters = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    problem_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
  };
  const [filters, setFilters] = useState(emptyFilters);

  // initial values define for spray problem field validation based on edit mode
  const initialFormValues: sprayProblemInterFace = {
    problem_name:
      editMode && selectedSprayProblem ? selectedSprayProblem.problem_name : "",
    problem_name_hi:
      editMode && selectedSprayProblem
        ? selectedSprayProblem.problem_name_hi || ""
        : "",
    problem_name_gu:
      editMode && selectedSprayProblem
        ? selectedSprayProblem.problem_name_gu || ""
        : "",
  };

  // Fetch spray problem list on page load
  useEffect(() => {
    getSprayProblemList();
  }, []);

  // spray problem list api call
  const getSprayProblemList = async () => {
    try {
      const response = await getAllDetails(GET_PROBLEM_LIST);

      if (response) {
        setSprayProblemList(response);
        setPageLoad(true);
      } else {
        setSprayProblemList([]);
        setPageLoad(false);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
      setPageLoad(false);
    }
  };

  // change spray problem status api call
  const changeProblemStatus = async (problemId: number, newStatus: boolean) => {
    try {
      const payload = {
        status: newStatus,
      };
      const response = await updateData(
        CHANGE_PROBLEM_STATUS,
        problemId,
        payload
      );

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message,
        });
        getSprayProblemList();
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    }
  };

  //  add & edit spray problem function api call
  const handleProblemSubmit = async (
    values: sprayProblemInterFace,
    { resetForm }: any
  ) => {
    setLoading(true);
    try {
      const payload: any = {
        problem_name: values.problem_name,
      };

      if (values.problem_name_hi && values.problem_name_hi.trim() !== "") {
        payload.problem_name_hi = values.problem_name_hi;
      }
      if (values.problem_name_gu && values.problem_name_gu.trim() !== "") {
        payload.problem_name_gu = values.problem_name_gu;
      }

      const response =
        editMode && selectedSprayProblem.id !== null
          ? await updateData(UPDATE_PROBLEM, selectedSprayProblem.id, payload)
          : await postAllData(ADD_PROBLEM, payload);

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message,
        });
        resetForm();
        setAddSprayProDialog(false);
        setEditMode(false);
        getSprayProblemList();
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // Edit button handler
  const onEdit = (rowData: sprayProblemInterFace) => {
    setEditMode(true);
    setSelectedSprayProblem(rowData);
    setAddSprayProDialog(true);
  };

  // Action column with edit icon
  const actionBodyTemplate = (rowData: sprayProblemInterFace) => (
    <div className="actions">
      <Button
        icon="pi pi-pencil"
        className="mr-2 p-button-secondary"
        tooltip="Edit"
        tooltipOptions={{ position: "top" }}
        onClick={() => onEdit(rowData)}
      />
    </div>
  );

  // Toolbar - Left
  const leftToolbarTemplate = () => (
    <>
      <span className="mr-0 md:mr-3 search-bar">
        <i className="pi pi-search" />
        <InputText
          type="search"
          className="search-input"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.currentTarget.value)}
          placeholder="Search..."
        />
      </span>
    </>
  );

  // Clear Filters
  const clear = () => {
    setGlobalFilter("");
    setFilters({ ...emptyFilters });
    setStatusFilterValue(null);
  };

  // Toolbar - Right
  const rightToolbarTemplate = () => (
    <>
      <Button
        icon="pi pi-filter-slash"
        label="Clear"
        onClick={() => clear()}
        className="md:mr-2 p-button-outlined p-button-primary"
      />
      <Button
        label="Add Problem"
        icon="pi pi-plus"
        className="ml-2 p-button toolbar-button"
        onClick={() => {
          setEditMode(false);
          setAddSprayProDialog(true);
        }}
      />
    </>
  );

  // Handle confirmed status change
  const handleConfirmStatusChange = () => {
    if (statusChangeData) {
      changeProblemStatus(statusChangeData.id, statusChangeData.newStatus);
      hideStatusDialog();
    }
  };

  // open confirm status change dialog
  const confirmChangeStatus = (id: number, newStatus: boolean) => {
    setStatusChangeData({ id, newStatus });
    setConfirmStatusDialog(true);
  };

  // Hide confirm dialog
  const hideStatusDialog = () => {
    setConfirmStatusDialog(false);
    setStatusChangeData(null);
  };

  //status Template
  const statusTemplate = (rowData: any) => {
    let statusClass =
      rowData.status === true
        ? "status status-active"
        : "status status-inactive";

    return (
      <div className="flex flex-column align-items-center">
        <InputSwitch
          checked={rowData.status}
          className="mb-2 mr-2"
          onChange={(e) => {
            confirmChangeStatus(rowData.id, e.value);
          }}
        />
        <span className={statusClass}>
          {rowData.status === true ? "Active" : "In Active"}
        </span>
      </div>
    );
  };

  //search by type Filter Template
  const searchByType = (options: any) => {
    return (
      <Dropdown
        filter
        value={statusFilterValue}
        options={statusDropdownItems}
        onChange={(e) => {
          setStatusFilterValue(e.value);
          options.filterCallback(e.value ? e.value.code : null, options.index);
        }}
        optionLabel="name"
        placeholder="Search By Status"
        className="order-drp"
        showClear
      />
    );
  };

  // Hide dialog and reset state
  const hideAddSprayProDialog = () => {
    setAddSprayProDialog(false);
    setEditMode(false);
  };

  // model footer button cancel & submit
  const footerContent = (
    <div>
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={hideAddSprayProDialog}
        className="p-button-secondary"
      />
      <Button
        label={editMode ? "Update" : "Save"}
        icon="pi pi-check"
        type="button"
        loading={loading}
        disabled={loading}
        onClick={() => formikRef.current?.handleSubmit()}
        autoFocus
      />
    </div>
  );

  return (
    <>
      <div className="grid crud-demo">
        <div className="col-12">
          <div className="card">
            <MasterTabs index={3} />
            <Toast ref={toast} />

            <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} />

            {pageLoad ? (
              <DataTable
                value={sprayProblemList}
                showGridlines
                stripedRows
                globalFilter={globalFilter || null}
                globalFilterFields={['problem_name']}
                rows={defaultRowOptions}
                paginator={sprayProblemList.length >= 10}
                rowsPerPageOptions={defaultPageRowOptions}
                className="datatable-responsive p-datatable-sm"
                paginatorTemplate={paginatorLinks}
                currentPageReportTemplate={showingEntries}
                filters={{ ...filters, global: { ...filters.global, value: globalFilter || null } }}
                onFilter={(e: any) => setFilters(e.filters)}
                emptyMessage="No Data Found"
                removableSort
              >
                {SprayProblemColumns.map((col) => {
                  if (col.field === "action") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        body={actionBodyTemplate}
                      />
                    );
                  } else if (col.field === "problem_name") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        sortable
                        filter
                        filterElement={globalNameFilterTemplate}
                        header={col.header}
                      />
                    );
                  } else if (col.field === "status") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        body={statusTemplate}
                        filter
                        filterElement={searchByType}
                      />
                    );
                  } else {
                    return (
                      <Column
                        key={col.field}
                        sortable
                        field={col.field}
                        header={col.header}
                      />
                    );
                  }
                })}
              </DataTable>
            ) : (
              <DataTable value={Skeletonitems}>
                {SprayProblemColumns.map((col) => (
                  <Column
                    key={col.field}
                    field={col.field}
                    header={col.header}
                    body={SkeletonbodyTemplate}
                  />
                ))}
              </DataTable>
            )}
          </div>
        </div>
      </div>
      {/* spray problem status confirm */}
      <Dialog
        visible={confirmStatusDialog}
        className="mx-3 lg:mx-0"
        style={{ width: "450px" }}
        draggable={false}
        header="Change Spray Problem Status"
        modal
        footer={
          <>
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-secondary"
              onClick={hideStatusDialog}
            />
            <Button
              label="Yes"
              icon="pi pi-check"
              className="p-button-primary"
              onClick={handleConfirmStatusChange}
              loading={loading}
            />
          </>
        }
        onHide={hideStatusDialog}
      >
        <div className="flex align-items-center justify-content-start">
          <i
            className="mr-3 pi pi-exclamation-triangle delete-triangle"
            style={{ fontSize: "2rem" }}
          />
          <span>
            Are you sure you want to set this spray problem Status as{" "}
            <b>{statusChangeData?.newStatus ? "Active" : "Inactive"}</b>?
          </span>
        </div>
      </Dialog>

      {/* Add/Edit spray problem Dialog with Formik */}
      <Dialog
        visible={addSprayProDialog}
        style={{ width: "450px" }}
        header={editMode ? "Edit Problem" : "Add Problem"}
        modal
        className="mx-3 lg:mx-0"
        draggable={false}
        onHide={hideAddSprayProDialog}
        footer={footerContent}
      >
        <Formik
          initialValues={initialFormValues}
          validationSchema={ProblemValidationSchema}
          enableReinitialize
          innerRef={formikRef}
          onSubmit={handleProblemSubmit}
        >
          {({
            handleSubmit,
            values,
            handleBlur,
            handleChange,
            touched,
            errors,
          }) => (
            <Form className="grid p-fluid" onSubmit={handleSubmit}>
              <div className="field col-12">
                <label htmlFor="problem_name">Name (English) *</label>
                <InputText
                  id="problem_name"
                  name="problem_name"
                  value={values.problem_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    touched.problem_name && errors.problem_name
                      ? "p-invalid"
                      : ""
                  }
                />

                <small className="p-error">
                  <ErrorMessage name="problem_name" />
                </small>
              </div>

              {/* Hindi problem Name */}
              <div className="field col-12">
                <label htmlFor="problem_name_hi">Name (Hindi)</label>
                <InputText
                  id="problem_name_hi"
                  name="problem_name_hi"
                  value={values.problem_name_hi}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>

              {/* Gujarati problem Name */}
              <div className="field col-12">
                <label htmlFor="problem_name_gu">Name (Gujarati)</label>
                <InputText
                  id="problem_name_gu"
                  name="problem_name_gu"
                  value={values.problem_name_gu}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
};

export default SprayForProblemList;
